import React from 'react';
import {
  EMIInstallmentPricingDetails,
  FlexibleInstallmentPricingDetails,
  FlexibleTenureEMIPricingDetails,
  PaymentCoreMachineSendType,
  PaymentCoreMachineStateType,
  PeriodicInstallmentPricingDetails
} from '@repo/payments-core';
import cn from 'classnames';
import { FormApi, FormStateBasic, FormValues } from 'informed';
import { getStateDetailsFromStateName } from 'libs/design-system/omega/src/utils/ComboBoxUtils';
import { useTranslation } from 'react-i18next';
import Yup from 'yup';

import { API_SUCCESS } from '@nw/api-constants';
import {
  ButtonComponent,
  getMinAndMaxDatesBasedOnVisibility,
  InformedBranchCombobox,
  InformedCheckbox,
  InformedCountriesCombobox,
  InformedDistrictsCombobox,
  InformedInstitutesCombobox,
  InformedInternationalPhoneNumberField,
  InformedOmegaCheckboxGroupField,
  InformedOmegaDatePicker,
  InformedPickerField,
  InformedRadioGroup,
  InformedStatesCombobox,
  InformedTextAreaField,
  InformedTextField,
  MultipleSelectField,
  radioGroupTypes,
  RadioGroupVariant,
  SlotSelectionField as SlotSelectionFieldComponent,
  useStateAndDistricts
} from '@nw/design-system/omega';
import { DiscountApplicableOnEnum } from '@nw/shared/data-access';
import {
  BannerField,
  ConditionallyDependentField as ConditionallyDependentFieldComponent,
  getRelevantLabel,
  getWrappedWithRelevantField,
  InformedFileUploaderFieldComponent,
  InformedFormVisitDateTimeField,
  ParamsFormField
} from '@nw/shared/ui';
import {
  convertNumberToCurrency,
  getS3FileUploaderUrl,
  isDateWithInRange,
  shouldEnableDataMaskBasedOnField
} from '@nw/shared/utils';

import BannerFieldModel from '../../stores/models/FieldModels/BannerField';
import BranchComboBoxField from '../../stores/models/FieldModels/BranchComboBoxField';
import CheckboxField from '../../stores/models/FieldModels/CheckboxField';
import ConditionallyDependentField from '../../stores/models/FieldModels/ConditionalDependentField';
import ContentField from '../../stores/models/FieldModels/ContentField';
import CountriesComboBoxField from '../../stores/models/FieldModels/CountriesComboBoxField';
import DatePickerFieldModel from '../../stores/models/FieldModels/DatePickerFieldModel';
import DistrictComboBoxFieldModel from '../../stores/models/FieldModels/DistrictComboBoxField';
import FileUploaderFieldModel from '../../stores/models/FieldModels/FileUploaderField';
import FormFieldGroup from '../../stores/models/FieldModels/FormFieldGroupField';
import FormVisitDateTimeField from '../../stores/models/FieldModels/FormVisitDateTime';
import InformedOTPFieldModel from '../../stores/models/FieldModels/InformedOTPfield';
import InstituteFormField from '../../stores/models/FieldModels/InstituteFormField';
import MultipleSelectFieldModel from '../../stores/models/FieldModels/MultipleSelectField';
import MultiSelectCheckboxGroupField from '../../stores/models/FieldModels/MultiSelectCheckboxGroupField';
import ParamsField from '../../stores/models/FieldModels/ParamsField';
import PhoneNumberField from '../../stores/models/FieldModels/PhoneNumberField';
import RadioGroupFormField from '../../stores/models/FieldModels/RadioGroupFormField';
import SelectField from '../../stores/models/FieldModels/SelectField';
import SlotSelectionField from '../../stores/models/FieldModels/SlotSelectionField';
import StatesComboBoxField from '../../stores/models/FieldModels/StatesComboBoxField';
import TextAreaField from '../../stores/models/FieldModels/TextareaField';
import TextFormField from '../../stores/models/FieldModels/TextFormField';
import { AllFormFieldModelsType } from '../../types/FieldModelTypes';
import { CustomClassNamesInterface } from '../../types/MiscellaneousTypes';
import { isAcademyUpfrontPlusEMI } from '../../utils/ProductUtils';

import {
  checkBoxFieldWrapperStyles,
  ContentFieldContainer,
  FieldContainer,
  fileUploaderLabelTextCSS,
  FormFieldGroupContainer,
  inputFieldMarginStyle,
  OtpButtonAndPhoneNumberWrapper,
  ProductPromotionalCodesFieldAndButtonWrapper,
  radioGroupfieldWrapperStyles,
  radioWrapperStyles,
  RequiredAsteriskStyles,
  SuccessInviteAndCouponCodeMessage
} from './styledComponent';

export interface InformedFieldsInfoInterface {
  formValues: FormStateBasic['values'];
  touched: FormStateBasic['touched'];
  errors: FormStateBasic['errors'];
  formApi: FormApi;
  stepFields: AllFormFieldModelsType[];
}

interface RenderAllProductFieldsProps {
  informedFieldsInfo: InformedFieldsInfoInterface;
  registrationFormState: any;
  registrationFormSend: any;
  validationSchemaObject: Yup.AnyObjectSchema;
  onBlurEachField?: any;
  customClassNames?: CustomClassNamesInterface;
  shouldDisableAllFields?: boolean;
}

export const RenderAllProductFields: React.FC<RenderAllProductFieldsProps> = ({
  informedFieldsInfo,
  registrationFormState,
  registrationFormSend,
  validationSchemaObject,
  onBlurEachField,
  customClassNames = {},
  shouldDisableAllFields = false
}): any => {
  const {
    formValues: values,
    errors,
    touched,
    stepFields,
    formApi
  } = informedFieldsInfo;

  const { states } = useStateAndDistricts();

  const { t } = useTranslation();

  const isInviteCodeAndCouponCodeButtonDisabled = (
    field: string,
    category: string
  ) => {
    const {
      context: {
        apiStatus: { checkCouponCodeApiStatus, checkInviteCodeApiStatus }
      }
    } = registrationFormState as PaymentCoreMachineStateType;

    if (
      errors[field] ||
      !values[field] ||
      checkCouponCodeApiStatus === 'loading' ||
      checkInviteCodeApiStatus === 'loading'
    ) {
      return true;
    }
    return false;
  };

  const isInviteCodeAndCouponCodeButtonLoading = (
    field: string,
    category: string
  ) => {
    const {
      context: {
        apiStatus: { checkCouponCodeApiStatus, checkInviteCodeApiStatus }
      }
    } = registrationFormState as PaymentCoreMachineStateType;
    if (category === 'invite_code') {
      return checkInviteCodeApiStatus === 'loading';
    } else if (category === 'coupon_code') {
      return checkCouponCodeApiStatus === 'loading';
    }
    return false;
  };

  const onClickInviteCodeAndCouponCodeButton = (
    field: string,
    category: string
  ) => {
    const {
      context: {
        isInviteCodeApplied,
        isCouponCodeApplied,
        errorMessages: {
          checkInviteCodeErrorMessage,
          checkCouponCodeErrorMessage
        }
      }
    } = registrationFormState as PaymentCoreMachineStateType;

    let buttonAction: 'apply' | 'remove';
    if (category === 'invite_code') {
      buttonAction =
        isInviteCodeApplied || checkInviteCodeErrorMessage ? 'remove' : 'apply';
      if (checkInviteCodeErrorMessage) {
        formApi.setValue(field, '');
      }
    } else {
      buttonAction =
        isCouponCodeApplied || checkCouponCodeErrorMessage ? 'remove' : 'apply';
      if (checkCouponCodeErrorMessage) {
        formApi.setValue(field, '');
      }
    }

    (registrationFormSend as PaymentCoreMachineSendType)({
      type: 'CHECK_REFERRAL_CODE',
      referralCode: values[field] as string,
      category: category === 'invite_code' ? 'invite_code' : 'coupon_code',
      action: buttonAction
    });
  };

  const renderInviteCodeAndCouponCodeButton = (
    field: string,
    category: string
  ) => {
    const {
      context: {
        isInviteCodeApplied,
        isCouponCodeApplied,
        isInviteCodeAutoApplied,
        errorMessages: {
          checkInviteCodeErrorMessage,
          checkCouponCodeErrorMessage
        }
      }
    } = registrationFormState as PaymentCoreMachineStateType;
    const getButtonText = () => {
      if (category === 'invite_code') {
        return isInviteCodeApplied || checkInviteCodeErrorMessage
          ? 'Clear'
          : 'Apply';
      } else {
        return isCouponCodeApplied || checkCouponCodeErrorMessage
          ? 'Clear'
          : 'Apply';
      }
    };

    if (isInviteCodeAutoApplied && category === 'invite_code') {
      return <></>;
    }

    return (
      <ButtonComponent
        key={`${field}-${category}`}
        type={'button'}
        variant="Primary"
        isDisabled={isInviteCodeAndCouponCodeButtonDisabled(field, category)}
        isLoading={isInviteCodeAndCouponCodeButtonLoading(field, category)}
        size="Small"
        onClick={() => onClickInviteCodeAndCouponCodeButton(field, category)}
        shouldShrinkButtonWhileLoading={false}
        className="product-promotional-code-apply-button"
      >
        {getButtonText()}
      </ButtonComponent>
    );
  };

  const renderSuccessInviteAndCouponCodeMessage = (
    field: string,
    category: string | undefined
  ) => {
    if (!category) {
      return null;
    }
    const {
      context: {
        isInviteCodeApplied,
        isCouponCodeApplied,
        selectedProductCode,
        productsDetailedInfo
      }
    } = registrationFormState as PaymentCoreMachineStateType;

    if (
      (!isInviteCodeApplied && !isCouponCodeApplied) ||
      !selectedProductCode ||
      !productsDetailedInfo
    ) {
      return null;
    }

    const productDetails =
      productsDetailedInfo[selectedProductCode].productDetails;

    if (productDetails) {
      const { referralDiscountObject, couponDiscountObject } =
        productDetails.currentPricingDetails;

      const discountObject =
        category === 'invite_code'
          ? referralDiscountObject
          : couponDiscountObject;

      if (!discountObject) {
        return null;
      }
      const getDiscountMessage = () => {
        const discountApplicableOn = discountObject.discount_applicable_on;
        const discountAmount = discountObject.discount_amount;
        switch (discountApplicableOn) {
          case DiscountApplicableOnEnum.enum.INSTALLMENTS_AMOUNT: {
            /**
             * Only for Upfront Plus EMI
             * referral code discount will be applied but success message will be different
             */

            if (isAcademyUpfrontPlusEMI(productDetails.productCode)) {
              return null;
            }
            if (
              productDetails.currentPricingDetails instanceof
                FlexibleInstallmentPricingDetails ||
              productDetails.currentPricingDetails instanceof
                EMIInstallmentPricingDetails ||
              productDetails.currentPricingDetails instanceof
                FlexibleTenureEMIPricingDetails ||
              productDetails.currentPricingDetails instanceof
                PeriodicInstallmentPricingDetails
            ) {
              const { totalAmountToBePaid } =
                productDetails.currentPricingDetails;
              return (
                discountAmount > 0 &&
                `${t('payment.discountCongrats')} ₹${convertNumberToCurrency(
                  discountAmount
                )}/- ${t('payment.discountApplied')}. ${t(
                  'payment.totalFeeIsNow'
                )} ₹${convertNumberToCurrency(totalAmountToBePaid)}/-`
              );
            }
            break;
          }
          case DiscountApplicableOnEnum.enum.FULL_PAYMENT_AMOUNT:
          case DiscountApplicableOnEnum.enum.DOWN_PAYMENT_AMOUNT:
            return (
              discountAmount > 0 &&
              `${t('payment.discountCongrats')} ₹${convertNumberToCurrency(
                discountAmount
              )}/- ${t('payment.discountApplied')}`
            );
        }
      };

      const discountMessage = getDiscountMessage();
      return (
        <SuccessInviteAndCouponCodeMessage>
          {discountMessage}
        </SuccessInviteAndCouponCodeMessage>
      );
    } else return null;
  };

  const getInviteCodeAndCouponCodeDisableStatus = (
    field: string,
    category: string
  ) => {
    const {
      context: {
        isInviteCodeApplied,
        isCouponCodeApplied,
        apiStatus: { checkInviteCodeApiStatus, checkCouponCodeApiStatus }
      }
    } = registrationFormState as PaymentCoreMachineStateType;

    if (category === 'invite_code') {
      return (
        isInviteCodeApplied ||
        checkCouponCodeApiStatus === 'loading' ||
        checkInviteCodeApiStatus === 'error'
      );
    } else if (category === 'coupon_code') {
      return (
        isCouponCodeApplied ||
        checkInviteCodeApiStatus === 'loading' ||
        checkCouponCodeApiStatus === 'error'
      );
    }
    return false;
  };

  const getAllStepFields = (allStepFields: AllFormFieldModelsType[]) => {
    return allStepFields.map(
      (eachField: AllFormFieldModelsType, index: number) => {
        const enableDataMask = shouldEnableDataMaskBasedOnField(
          eachField.field
        );

        return eachField.match({
          TextFormField: (fieldData: TextFormField) => {
            const {
              isHidden,
              helperText,
              field,
              label,
              isRelevant,
              relevantCondition,
              isFieldDisabled,
              shouldShowIcon,
              focusIcon,
              reRenderedFields,
              placeholder,
              shouldShowOtpField,
              shouldShowLabel,
              shouldShowErrorIcon,
              shouldVerifyEmail,
              fieldType,
              isRequired,
              dependentOnFields,
              category
            } = fieldData;

            const reRenderedFieldsObjects = {
              shouldShowOTPButton: null,
              getOTPButtonStatus: API_SUCCESS
            };

            const isFieldDisable = () => {
              if (isFieldDisabled) {
                return isFieldDisabled;
              } else if (
                category === 'invite_code' ||
                category === 'coupon_code'
              ) {
                return getInviteCodeAndCouponCodeDisableStatus(field, category);
              }
              return false;
            };

            const getInitialValue = () => {
              if (category === 'invite_code') {
                if (
                  registrationFormState.context.promotionalCodes.invite_code
                ) {
                  return registrationFormState.context.promotionalCodes
                    .invite_code;
                }
              } else if (category === 'coupon_code') {
                if (
                  registrationFormState.context.promotionalCodes.coupon_code
                ) {
                  return registrationFormState.context.promotionalCodes
                    .coupon_code;
                }
              }
            };

            const getErrorMessage = () => {
              if (category === 'invite_code') {
                return registrationFormState.context.errorMessages
                  .checkInviteCodeErrorMessage;
              } else if (category === 'coupon_code') {
                return registrationFormState.context.errorMessages
                  .checkCouponCodeErrorMessage;
              }
              return null;
            };

            const inputField = (
              <>
                <FieldContainer css={isRequired ? RequiredAsteriskStyles : ''}>
                  <ProductPromotionalCodesFieldAndButtonWrapper>
                    <InformedTextField
                      inputClassName={'informedInputField'}
                      inputContainerClassName={
                        'informedInputContainerClassName'
                      }
                      field={field}
                      key={field}
                      label={label}
                      fieldType={fieldType}
                      validationSchemaObject={validationSchemaObject}
                      isDisabled={
                        isFieldDisable() || Boolean(shouldDisableAllFields)
                      }
                      shouldShowIcon={shouldShowIcon}
                      placeholder={placeholder}
                      helperText={helperText}
                      error={errors[field] || getErrorMessage()}
                      touched={touched[field]}
                      fieldWrapperStyles={inputFieldMarginStyle}
                      focusIcon={focusIcon}
                      isHidden={isHidden}
                      reRenderedFieldsObjects={reRenderedFieldsObjects}
                      showErrorIcon={
                        !(
                          shouldShowOtpField &&
                          registrationFormState.context.shouldShowGetOTP
                        )
                      }
                      labelStyles={
                        shouldShowLabel
                          ? cn('mb-1 flex', 'informedInputLabelText')
                          : cn('mb-1 hidden')
                      }
                      validateOnBlur={true}
                      isMarginBottomZero={true}
                      shouldShowLabel={shouldShowLabel}
                      enableDataMask={enableDataMask}
                      hintClassName={
                        errors[field] ? '' : 'textFieldHelperTextClassName'
                      }
                      shouldShowErrorIcon={shouldShowErrorIcon}
                      handleBlur={() => {
                        onBlurEachField && onBlurEachField(field);
                      }}
                      dependentOnFields={dependentOnFields}
                      values={values}
                      touchedFields={touched}
                      value={getInitialValue()}
                    />
                    {category === 'invite_code' || category === 'coupon_code'
                      ? renderInviteCodeAndCouponCodeButton(field, category)
                      : null}
                  </ProductPromotionalCodesFieldAndButtonWrapper>
                  {renderSuccessInviteAndCouponCodeMessage(field, category)}
                </FieldContainer>
              </>
            );
            return getWrappedWithRelevantField({
              field: field,
              isRelevant: isRelevant,
              relevantCondition: relevantCondition,
              component: inputField
            });
          },
          PhoneNumberField: (fieldData: PhoneNumberField) => {
            const {
              isHidden,
              helperText,
              field,
              label,
              isRelevant,
              relevantCondition,
              isFieldDisabled,
              reRenderedFields,
              placeholder,
              shouldShowOtpField,
              shouldShowLabel,
              getOtpButtonActions,
              fieldType,
              isRequired
            } = fieldData;

            if (shouldShowOtpField) {
              registrationFormState.context.shouldShowGetOTP = true;
            }
            const reRenderedFieldsObjects: Record<string, any> = {
              shouldShowOTPButton: null,
              getOTPButtonStatus: API_SUCCESS
            };
            reRenderedFields.forEach(
              (eachField) =>
                (reRenderedFieldsObjects[eachField] = values[eachField])
            );

            const {
              context: {
                isOtpVerified,
                initialValues,
                fieldsAuthMachineRefDetails,
                isOtpSentToUser
              }
            } = registrationFormState;

            const shouldDisableGetOtpButton = () => {
              if (isFieldDisabled) {
                return isFieldDisabled;
              } else if (initialValues?.[field]) {
                return true;
              }
              return false;
            };

            const shouldDisableInputField = () => {
              if (isFieldDisabled) {
                return isFieldDisabled;
              } else if (initialValues?.[field]) {
                return true;
              } else if (isOtpSentToUser) {
                return true;
              }
              return false;
            };

            const isOTPVerified = () => {
              if (
                isOtpVerified ||
                fieldsAuthMachineRefDetails?.[field]?.state.context
                  .isOtpVerified
              ) {
                return true;
              }
              return false;
            };

            const fieldValidations = {
              validateOnBlur: true,
              validateOnChange: false
            };

            const internationalPhoneNumber = (
              <FieldContainer
                isMarginBottomZero={true}
                css={isRequired ? RequiredAsteriskStyles : ''}
              >
                <OtpButtonAndPhoneNumberWrapper>
                  <InformedInternationalPhoneNumberField
                    label={label}
                    field={field}
                    error={errors[field]}
                    touched={touched[field]}
                    validationSchemaObject={validationSchemaObject}
                    isDisabled={
                      shouldDisableInputField() ||
                      Boolean(shouldDisableAllFields)
                    }
                    wrapperWithPopOverStyles={cn('border-none p-0')}
                    popOverStyle={'secondary'}
                    values={values}
                    t={t}
                    fieldValidations={fieldValidations}
                    allowedCountriesList={['IN']}
                    isHidden={isHidden}
                    helperText={helperText}
                    placeholder={placeholder}
                    labelStyles={
                      shouldShowLabel
                        ? cn('mb-1 flex', 'informedInputLabelText')
                        : cn('mb-1 hidden')
                    }
                    helperTextStyles={
                      errors[field] ? '' : 'phoneNumberFieldHelperTextClassName'
                    }
                    phoneNumberInputStyles={cn('informedInputField')}
                    intlPhoneNumberFieldStyles={cn(
                      'informedPhoneNumberContainerClassName'
                    )}
                  />
                </OtpButtonAndPhoneNumberWrapper>
              </FieldContainer>
            );

            return getWrappedWithRelevantField({
              field: field,
              isRelevant: isRelevant,
              relevantCondition: relevantCondition,
              component: internationalPhoneNumber
            });
          },
          InformedOTPField: (fieldData: InformedOTPFieldModel) => {
            return <></>;
          },
          SelectField: (fieldData: SelectField) => {
            const {
              label,
              field,
              options,
              isHidden,
              isRelevant,
              relevantCondition,
              placeholder,
              shouldShowLabel,
              helperText,
              relevantLabel,
              dependentOnOptionConfigs,
              isRequired
            } = fieldData;

            const dependentFieldName: string =
              dependentOnOptionConfigs?.dependent_on_field_name_enum ?? '';

            const isFieldDisabled = () => {
              if (dependentOnOptionConfigs) {
                if (!values[dependentFieldName]) {
                  return true;
                } else return false;
              }
              return false;
            };

            let updatedOptions: typeof options | undefined = options;

            if (dependentOnOptionConfigs) {
              updatedOptions =
                dependentOnOptionConfigs.dependent_on_options.find(
                  (dependentOption) =>
                    dependentOption.dependent_on_field_value ===
                    values[dependentFieldName]
                )?.options;
              if (!updatedOptions) updatedOptions = [];
            }

            let updatedLabel = label;
            if (relevantLabel) {
              updatedLabel = getRelevantLabel({
                relevantLabel: relevantLabel,
                values: values
              });
            }

            const reRenderedFieldsObjects = {
              dependentOptionValue: dependentOnOptionConfigs
                ? values[dependentFieldName]
                : ''
            };

            const selectField = (
              <FieldContainer
                id={field}
                css={isRequired ? RequiredAsteriskStyles : ''}
              >
                <InformedPickerField
                  isDisabled={
                    isFieldDisabled() || Boolean(shouldDisableAllFields)
                  }
                  isHidden={isHidden}
                  key={field}
                  field={field}
                  validationSchemaObject={validationSchemaObject}
                  label={updatedLabel}
                  options={updatedOptions as any}
                  error={errors[field]}
                  touched={touched[field]}
                  reRenderObjects={
                    dependentOnOptionConfigs ? [reRenderedFieldsObjects] : []
                  }
                  placeholder={placeholder ? placeholder : ''}
                  validateOnBlur={false}
                  onChangeOption={() => {
                    onBlurEachField && onBlurEachField(field);
                  }}
                  isSearchable={false}
                  wrapperWithPopOverStyles={cn(
                    'w-full max-h-200px',
                    'informedInputContainerClassName'
                  )}
                  inputGroupStyles={cn('w-full')}
                  buttonStyles={cn('w-full h-36px')}
                  labelStyles={
                    shouldShowLabel
                      ? cn('mb-1 flex', 'informedInputLabelText')
                      : cn('mb-1 hidden')
                  }
                  listBoxStyles={cn(
                    'max-h-162px',
                    'informedInputContainerClassName'
                  )}
                  inputFieldStyles={cn('truncate', 'informedInputField')}
                  isMarginBottomZero={true}
                  errorMessageStyles={cn('h-16px')}
                  enableDataMask={enableDataMask}
                  helperText={helperText}
                />
              </FieldContainer>
            );
            return getWrappedWithRelevantField({
              field: field,
              isRelevant: isRelevant,
              relevantCondition: relevantCondition,
              component: selectField
            });
          },
          StatesComboBoxField: (fieldData: StatesComboBoxField) => {
            const {
              field,
              label,
              placeholder,
              mobilePlaceholder,
              isRequired,
              relevantCondition,
              isRelevant
            } = fieldData;

            const { context } = registrationFormState;

            const initialValue =
              values[field] ??
              context.initialValues?.[field] ??
              context?.userProfileDetails?.['currentState'];

            const informStateComboBox = (
              <FieldContainer css={isRequired ? RequiredAsteriskStyles : ''}>
                <InformedStatesCombobox
                  key={field}
                  label={label}
                  placeholder={placeholder}
                  mobilePlaceholder={mobilePlaceholder}
                  isDisabled={Boolean(shouldDisableAllFields)}
                  field={field}
                  touched={touched[field]}
                  error={errors[field]}
                  validationSchemaObject={validationSchemaObject}
                  getDisplayValueFromSelectedState={(state) => state.state_name}
                  hintClassName={
                    errors[field] ? '' : cn('statesComboboxHintClassName')
                  }
                  initialStateValue={initialValue as string}
                  onSelectionChange={() => {
                    onBlurEachField && onBlurEachField(field);
                  }}
                />
              </FieldContainer>
            );

            return getWrappedWithRelevantField({
              field,
              isRelevant: isRelevant,
              relevantCondition: relevantCondition,
              component: informStateComboBox
            });
          },
          DistrictComboBoxField: (fieldData: DistrictComboBoxFieldModel) => {
            const {
              isHidden,
              field,
              label,
              isRelevant,
              relevantCondition,
              searchKey,
              isDependent,
              dependentOnField,
              helperText,
              isFieldDisabled,
              placeholder,
              stateFieldName,
              isRequired,
              initialValue
            } = fieldData;
            const reRenderedFieldsObjects = {
              searchKey: isDependent ? values[dependentOnField] : searchKey
            };

            const isDisabled =
              !values[stateFieldName] || Boolean(shouldDisableAllFields);
            const selectedState = getStateDetailsFromStateName(
              values[stateFieldName] as any,
              states.current
            );

            const { context } = registrationFormState;

            const getInitialValue = () => {
              if (values[field]) return values[field];
              if (initialValue) {
                return initialValue;
              } else if (context.initialValues?.[field]) {
                return context.initialValues?.[field].district_name
                  ? context.initialValues?.[field].district_name
                  : context.initialValues?.[field];
              }
              return initialValue;
            };

            const districtComboboxField = (
              <FieldContainer css={isRequired ? RequiredAsteriskStyles : ''}>
                <InformedDistrictsCombobox
                  key={field}
                  label={label}
                  placeholder={placeholder ?? t('placeholder.select')}
                  isHidden={isHidden}
                  isDisabled={
                    isFieldDisabled ||
                    isDisabled ||
                    Boolean(shouldDisableAllFields)
                  }
                  field={field}
                  touched={touched[field]}
                  error={errors[field]}
                  helperText={helperText}
                  validationSchemaObject={validationSchemaObject}
                  reRenderObjects={[reRenderedFieldsObjects]}
                  getDisplayValueFromSelectedState={(district) =>
                    district.district_name
                  }
                  stateId={selectedState?.state_id || ''}
                  initialDistrictValue={getInitialValue()}
                  isMarginBottomZero={true}
                  enableDataMask={enableDataMask}
                  onSelectionChange={() => {
                    onBlurEachField && onBlurEachField(field);
                  }}
                />
              </FieldContainer>
            );

            return getWrappedWithRelevantField({
              field,
              isRelevant,
              relevantCondition,
              component: districtComboboxField
            });
          },
          BranchComboBoxField: (fieldData: BranchComboBoxField) => {
            const {
              field,
              isHidden,
              label,
              placeholder,
              supportingField,
              shouldShowLabel,
              mobilePlaceholder,
              isRelevant,
              relevantCondition,
              isRequired
            } = fieldData;

            const initialBranchValue =
              supportingField && values[supportingField]
                ? values[supportingField]
                : values[field];

            const branchComboboxField = (
              <FieldContainer css={isRequired ? RequiredAsteriskStyles : ''}>
                <InformedBranchCombobox
                  key={field}
                  isHidden={isHidden}
                  field={field}
                  label={shouldShowLabel ? label : ''}
                  validationSchemaObject={validationSchemaObject}
                  touched={touched[field]}
                  placeholder={placeholder}
                  mobilePlaceholder={mobilePlaceholder}
                  supportingField={supportingField ?? ''}
                  enableDataMask={enableDataMask}
                  initialBranchValue={initialBranchValue as string}
                  onBlur={() => {
                    onBlurEachField && onBlurEachField(field);
                  }}
                />
              </FieldContainer>
            );

            return getWrappedWithRelevantField({
              field: field,
              isRelevant: isRelevant,
              relevantCondition: relevantCondition,
              component: branchComboboxField
            });
          },
          CheckBoxField: (fieldData: CheckboxField) => {
            const {
              field,
              label,
              isHidden,
              isRelevant,
              relevantCondition,
              shouldHideReceiveUpdates,
              shouldShowOnlyLabel,
              isRequired
            } = fieldData;
            const checkBoxField = (
              <FieldContainer css={isRequired ? RequiredAsteriskStyles : ''}>
                <InformedCheckbox
                  field={field}
                  key={field}
                  label={label}
                  isHidden={isHidden}
                  error={errors[field]}
                  touched={touched[field]}
                  validationSchemaObject={validationSchemaObject}
                  fieldWrapperStyles={checkBoxFieldWrapperStyles}
                  isInnerHTML={true}
                  shouldHideReceiveUpdates={shouldHideReceiveUpdates}
                  validateOnBlur={false}
                  labelClassName={cn('informedInputLabelText')}
                  shouldShowOnlyLabel={shouldShowOnlyLabel}
                  onBlur={() => {
                    onBlurEachField && onBlurEachField(field);
                  }}
                />
              </FieldContainer>
            );
            return getWrappedWithRelevantField({
              field,
              isRelevant,
              relevantCondition,
              component: checkBoxField
            });
          },
          ParamsField: (fieldData: ParamsField) => {
            const { field, isHidden } = fieldData;
            return <ParamsFormField field={field} isHidden={isHidden} />;
          },
          InstituteField: (fieldData: InstituteFormField) => {
            const {
              field,
              isHidden,
              label,
              placeholder,
              supportingField,
              shouldShowLabel,
              mobilePlaceholder,
              isRelevant,
              relevantCondition,
              isRequired
            } = fieldData;

            const institutesCombobox = (
              <FieldContainer css={isRequired ? RequiredAsteriskStyles : ''}>
                <InformedInstitutesCombobox
                  isHidden={isHidden}
                  field={field}
                  label={shouldShowLabel ? label : ''}
                  validationSchemaObject={validationSchemaObject}
                  touched={touched[field]}
                  placeholder={placeholder}
                  mobilePlaceholder={mobilePlaceholder}
                  supportingField={supportingField}
                  enableDataMask={enableDataMask}
                  initialInstituteValue={
                    values[supportingField]
                      ? (values[supportingField] as string)
                      : (values[field] as string)
                  }
                  onBlur={() => {
                    onBlurEachField && onBlurEachField(field);
                  }}
                />
              </FieldContainer>
            );

            return getWrappedWithRelevantField({
              field: field,
              isRelevant: isRelevant,
              relevantCondition: relevantCondition,
              component: institutesCombobox
            });
          },
          SlotSelectionField: (fieldData: SlotSelectionField) => {
            const {
              field,
              supportingField,
              label,
              slotOptions,
              dayWiseSlotHoldDates,
              slotConditions,
              intensiveSlotsLanguageOptions,
              academySlotsLanguageOptions,
              isDependent,
              dependentOnField,
              slotUIType,
              relevantCondition,
              isRelevant,
              isRequired,
              dependentField,
              isDateBasedSlots
            } = fieldData;

            const slotSelectionField = (
              <FieldContainer css={isRequired ? RequiredAsteriskStyles : ''}>
                <SlotSelectionFieldComponent
                  field={field}
                  label={label}
                  slotOptions={slotOptions}
                  touched={touched[field]}
                  error={errors[field]}
                  validateOnBlur={false}
                  validateOnChange={true}
                  isDependent={isDependent}
                  dependentFieldValue={values[dependentOnField] as string}
                  isDependentFieldTouched={touched[dependentOnField]}
                  supportingField={supportingField}
                  dayWiseSlotHoldDates={dayWiseSlotHoldDates}
                  slotConditions={slotConditions}
                  intensiveSlotsLanguageOptions={intensiveSlotsLanguageOptions}
                  academySlotsLanguageOptions={academySlotsLanguageOptions}
                  validationSchemaObject={validationSchemaObject}
                  values={values}
                  slotUIType={slotUIType}
                  dependentOnFields={dependentField}
                  isDateBasedSlots={isDateBasedSlots}
                />
              </FieldContainer>
            );

            return getWrappedWithRelevantField({
              field,
              isRelevant,
              relevantCondition,
              component: slotSelectionField
            });
          },
          ConditionallyDependentField: (
            fieldData: ConditionallyDependentField
          ) => {
            const {
              field,
              dependentOnFields,
              conditionallyDependentValues,
              relevantCondition,
              isRelevant
            } = fieldData;

            const conditionallyDependentField = (
              <ConditionallyDependentFieldComponent
                field={field}
                values={values}
                dependentOnFields={dependentOnFields}
                conditionallyDependentValues={conditionallyDependentValues}
              />
            );
            return getWrappedWithRelevantField({
              field,
              isRelevant,
              relevantCondition,
              component: conditionallyDependentField
            });
          },
          RadioGroupFormField: (fieldData: RadioGroupFormField) => {
            const {
              isHidden,
              field,
              label,
              isRelevant,
              relevantCondition,
              options,
              isRequired,
              defaultValue,
              hideRadioButtons,
              isCustomizedRadioOptions,
              optionsType
            } = fieldData;
            const radioOptions = options && options.length > 0 ? options : [];
            const radioGroupField = (
              <FieldContainer css={isRequired ? RequiredAsteriskStyles : ''}>
                <InformedRadioGroup
                  label={label}
                  isDisabled={Boolean(shouldDisableAllFields)}
                  validationSchemaObject={validationSchemaObject}
                  field={field}
                  key={field}
                  values={values}
                  error={errors[field]}
                  touched={touched[field]}
                  isHidden={isHidden}
                  radioOptions={radioOptions}
                  fieldWrapperStyles={radioGroupfieldWrapperStyles}
                  radioWrapperStyles={radioWrapperStyles}
                  validateOnBlur={false}
                  validateOnChange={false}
                  type={radioGroupTypes.simple as RadioGroupVariant}
                  defaultValue={defaultValue as string}
                  hideRadioButtons={hideRadioButtons}
                  onChange={(value) => {
                    onBlurEachField && onBlurEachField(field, value);
                  }}
                  isCustomizedRadioOptions={isCustomizedRadioOptions}
                  optionsType={optionsType}
                />
              </FieldContainer>
            );
            return getWrappedWithRelevantField({
              field,
              isRelevant,
              relevantCondition,
              component: radioGroupField
            });
          },
          MultiSelectCheckboxGroupField: (
            fieldData: MultiSelectCheckboxGroupField
          ) => {
            const {
              field,
              label,
              options,
              placeholder,
              isRelevant,
              relevantCondition
            } = fieldData;

            const multiSelectCheckboxGroup = (
              <InformedOmegaCheckboxGroupField
                label={label}
                options={options}
                placeholder={placeholder ?? ''}
                field={field}
                error={errors[field]}
                touched={touched[field]}
                validationSchemaObject={validationSchemaObject}
              />
            );

            return getWrappedWithRelevantField({
              field,
              isRelevant,
              relevantCondition,
              component: multiSelectCheckboxGroup
            });
          },
          BannerField: (fieldData: BannerFieldModel) => {
            const {
              field,
              isCloseAllow,
              imgLink,
              altText,
              description,
              heading,
              isRelevant,
              relevantCondition
            } = fieldData;
            const bannerField = (
              <BannerField
                isCloseAllow={isCloseAllow}
                imgLink={imgLink}
                altText={altText}
                description={description}
                heading={heading}
              />
            );

            return getWrappedWithRelevantField({
              field,
              isRelevant,
              relevantCondition,
              component: bannerField
            });
          },
          MultipleSelectField: (fieldData: MultipleSelectFieldModel) => {
            const {
              field,
              options,
              label,
              placeholder,
              minimumSelectFieldAllowed,
              initialSelectFieldNumber,
              addSelectFieldText,
              relevantCondition,
              isRelevant,
              dependentOnFields
            } = fieldData;

            const formattedOptions = options.map((option) => {
              return {
                value: option.label,
                label: option.label
              };
            });

            const multipleSelectField = (
              <MultipleSelectField
                field={field}
                values={values}
                options={options}
                label={label}
                placeholder={placeholder ?? ''}
                error={errors[field]}
                touched={touched[field]}
                minimumSelectFieldAllowed={minimumSelectFieldAllowed}
                initialSelectFieldNumber={initialSelectFieldNumber}
                validationSchemaObject={validationSchemaObject}
                addSelectFieldText={addSelectFieldText}
                isDisabled={Boolean(shouldDisableAllFields)}
                dependentOnFields={dependentOnFields}
                formattedOptions={formattedOptions}
              />
            );

            return getWrappedWithRelevantField({
              field,
              isRelevant,
              relevantCondition,
              component: multipleSelectField
            });
          },
          ContentField: (fieldData: ContentField) => {
            const { field, isHidden, label, isRelevant, relevantCondition } =
              fieldData;
            const contentFieldComponent = (
              <ContentFieldContainer
                isHidden={isHidden}
                className={'content-field-container'}
                key={field}
                dangerouslySetInnerHTML={{
                  __html: label
                }}
              />
            );
            return getWrappedWithRelevantField({
              field,
              isRelevant,
              relevantCondition,
              component: contentFieldComponent
            });
          },
          TextAreaField: (fieldData: TextAreaField) => {
            const {
              field,
              label,
              helperText,
              isMarginBottomZero,
              isFieldDisabled,
              placeholder,
              isHidden
            } = fieldData;
            return (
              <InformedTextAreaField
                field={field}
                label={label}
                helperText={helperText}
                isDisabled={isFieldDisabled || Boolean(shouldDisableAllFields)}
                validationSchemaObject={validationSchemaObject}
                error={errors[field]}
                isHidden={isHidden}
                touched={touched[field]}
                rows={4}
                enableDataMask={enableDataMask}
                isMarginBottomZero={isMarginBottomZero}
                placeHolder={placeholder}
              />
            );
          },
          FormFieldGroup: (fieldData: FormFieldGroup) => {
            const { formFields } = fieldData;
            const formFieldsGroupComponent = (
              <FormFieldGroupContainer className="form-fields-group-container">
                {getAllStepFields(formFields)}
              </FormFieldGroupContainer>
            );
            return formFieldsGroupComponent;
          },
          FileUploaderField: (fieldData: FileUploaderFieldModel) => {
            const {
              field,
              label,
              pathToUpload,
              isRelevant,
              relevantCondition,
              aclType,
              isRequired
            } = fieldData;

            const fileUploaderComponent: any = (
              <FieldContainer css={isRequired ? RequiredAsteriskStyles : ''}>
                <InformedFileUploaderFieldComponent
                  field={field}
                  isDisabled={false}
                  error={errors[field]}
                  touched={touched[field]}
                  label={label}
                  fileName={pathToUpload}
                  validationSchemaObject={validationSchemaObject}
                  aclType={aclType}
                  getS3FileUploaderUrl={getS3FileUploaderUrl}
                  labelTextCSS={isRequired && fileUploaderLabelTextCSS}
                  onFileUpload={() => {
                    onBlurEachField && onBlurEachField(field);
                  }}
                />
              </FieldContainer>
            );

            return getWrappedWithRelevantField({
              field,
              isRelevant,
              relevantCondition,
              component: fileUploaderComponent
            });
          },
          DatePickerField: (fieldData: DatePickerFieldModel) => {
            const {
              field,
              label,
              placeholder,
              showMonthYearPicker,
              isRequired,
              maxDate,
              minDate,
              excludeDates,
              maxVisibilityTime,
              dateFormat
            } = fieldData;
            const { minimumDate, maximumDate } =
              getMinAndMaxDatesBasedOnVisibility(
                minDate,
                maxDate,
                maxVisibilityTime
              );

            const getDayClassName = (
              date: Date,
              isSelectedDate?: Date
            ): string => {
              if (minimumDate && maximumDate) {
                const isDateInGivenRange = isDateWithInRange(
                  date,
                  new Date(minimumDate),
                  new Date(maximumDate)
                );
                if (!isSelectedDate) {
                  return isDateInGivenRange
                    ? 'customizedDayClassName'
                    : 'customizedDayDisabledClassName';
                }
                return 'customizedSelectedDayClassName';
              }
              return '';
            };

            return (
              <FieldContainer css={isRequired ? RequiredAsteriskStyles : ''}>
                <InformedOmegaDatePicker
                  field={field}
                  label={label}
                  placeholder={placeholder}
                  value={values[field] as string}
                  validationSchemaObject={validationSchemaObject}
                  showMonthDropdown={showMonthYearPicker}
                  showYearDropdown={showMonthYearPicker}
                  excludeDates={excludeDates}
                  maxDate={maximumDate}
                  minDate={minimumDate}
                  dateFormat={dateFormat}
                  dayClassName={getDayClassName}
                  onChange={() => {}}
                  onSelectedDate={() => {
                    onBlurEachField && onBlurEachField(field);
                  }}
                  error={errors[field]}
                  touched={touched[field]}
                  disabled={false}
                  labelStyles={'informedInputLabelText'}
                />
              </FieldContainer>
            );
          },
          CountriesComboBoxField: (fieldData: CountriesComboBoxField) => {
            const {
              field,
              label,
              placeholder,
              mobilePlaceholder,
              initialValue,
              isRequired
            } = fieldData;

            const { context } = registrationFormState;

            const getInitialValue = () => {
              if (values[field]) return values[field];
              else if (context.initialValues?.[field])
                return context.initialValues?.[field];
              else if (initialValue) {
                return initialValue;
              } else return '';
            };

            const initialCountryValue = getInitialValue();

            const informedCountriesComboBox = (
              <FieldContainer css={isRequired ? RequiredAsteriskStyles : ''}>
                <InformedCountriesCombobox
                  key={field}
                  label={label}
                  placeholder={placeholder}
                  mobilePlaceholder={mobilePlaceholder}
                  field={field}
                  touched={touched[field]}
                  error={errors[field]}
                  validationSchemaObject={validationSchemaObject}
                  getDisplayValueFromSelectedCountry={(country) =>
                    country.country_name
                  }
                  hintClassName={
                    errors[field] ? '' : cn('statesComboboxHintClassName')
                  }
                  initialCountryValue={initialCountryValue as string}
                  onSelectionChange={() => {
                    onBlurEachField && onBlurEachField(field);
                  }}
                />
              </FieldContainer>
            );
            return getWrappedWithRelevantField({
              field,
              isRelevant: true,
              relevantCondition: true,
              component: informedCountriesComboBox
            });
          },
          FormVisitDateTimeField: (formField: FormVisitDateTimeField) => {
            const { field, isRelevant, relevantCondition } = formField;
            const formVisitDateTimeField = (
              <InformedFormVisitDateTimeField key={field} field={field} />
            );
            return getWrappedWithRelevantField({
              field,
              isRelevant,
              relevantCondition,
              component: formVisitDateTimeField
            });
          }
        });
      }
    );
  };
  const allStepFields = getAllStepFields(stepFields);

  return allStepFields;
};
